import type { ApolloLink } from '@apollo/client/link';
import { ErrorLink } from '@apollo/client/link/error';
import { from, mergeMap, of, tap, throwError } from 'rxjs';

import { API_ERROR_CODES, isUnauthenticatedError } from '@/api/errors';
import { clearSession, fromBearer, getFreshAccessToken } from '@/api/session';
import { getUserToken } from '@/shared/services/user-hash';

/**
 * Операции самой сессии: их отказ по токену — штатный ответ (неверный пароль,
 * протухшая refresh-кука), а не повод обновлять токен.
 */
const SESSION_OPERATIONS = new Set(['Login', 'Refresh', 'Logout']);

/** В ответе есть GraphQL-ошибка `UNAUTHENTICATED`. */
const hasUnauthenticatedError = (result: ApolloLink.Result): boolean =>
  Boolean(
    result.errors?.some(
      (error) => error.extensions?.code === API_ERROR_CODES.unauthenticated
    )
  );

/**
 * Сбрасывает сессию, если она ещё есть: из пачки параллельных отказов
 * сбрасывает только первый (токен удаляется синхронно).
 */
const expireSession = (operation: ApolloLink.Operation): void => {
  if (getUserToken().accessToken) void clearSession(operation.client);
};

/**
 * Централизованная реакция на отказ по токену: обновляем accessToken по
 * refresh-куке и повторяем запрос с новым. Не удался refresh или повтор снова
 * отклонён — сессия истекла, сбрасываем её. Остальные ошибки не трогает — их
 * обрабатывает вызывающий хук (текст через `getApiErrorMessage`).
 */
export const errorLink = new ErrorLink(
  ({ error, result, operation, forward }) => {
    if (
      !isUnauthenticatedError(error) ||
      SESSION_OPERATIONS.has(operation.operationName ?? '')
    ) {
      return;
    }

    // Запрос ушёл без токена — это не истечение сессии, обновлять нечего.
    const rejectedToken = fromBearer(
      operation.getContext().headers?.Authorization
    );
    if (!rejectedToken) return;

    const isRefreshed = getFreshAccessToken(
      operation.client,
      rejectedToken
    ).then(
      () => true,
      () => {
        expireSession(operation);
        return false;
      }
    );

    return from(isRefreshed).pipe(
      mergeMap((refreshed) => {
        // Вызывающий получает исходный отказ, как без попытки refresh.
        if (!refreshed) return result ? of(result) : throwError(() => error);

        // Повтор идёт дальше по цепочке, минуя этот link: authLink подставит новый токен.
        return forward(operation).pipe(
          tap({
            next: (retried) => {
              if (hasUnauthenticatedError(retried)) expireSession(operation);
            },
            error: (retryError: unknown) => {
              if (isUnauthenticatedError(retryError)) expireSession(operation);
            }
          })
        );
      })
    );
  }
);
